import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, RefreshCw, Tag, Users, Plane, Route, Star, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Common airline codes
const AIRLINES = [
  { code: '6E', name: 'IndiGo' },
  { code: 'AI', name: 'Air India' },
  { code: 'SG', name: 'SpiceJet' },
  { code: 'UK', name: 'Vistara' },
  { code: 'I5', name: 'AirAsia India' },
  { code: 'G8', name: 'Go First' },
  { code: 'EK', name: 'Emirates' },
  { code: 'QR', name: 'Qatar Airways' },
  { code: 'EY', name: 'Etihad' },
  { code: 'SQ', name: 'Singapore Airlines' },
];

const TRAVEL_CLASSES = ['Economy', 'Premium Economy', 'Business', 'First Class'];

const MarkupSettings = () => {
  const [rules, setRules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    user_type: 'agent',
    category: 'airline',
    value: 0,
    value_type: 'percentage',
    airline_code: '',
    route: '',
    travel_class: '',
    flight_type: '',  // NEW: domestic/international
    trip_type: '',    // NEW: oneway/return
    special_agents: [],
    priority: 1,
    valid_from: '',
    valid_to: ''
  });
  
  // Stats
  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    agent_rules: 0, 
    b2c_rules: 0 
  });

  const getAdminToken = () => {
    
    return token ? `Bearer ${token}` : '';
  };

  // Fetch markup rules
  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/markup`, {
        withCredentials: true,
        params: {
          user_type: userTypeFilter || undefined,
          category: categoryFilter || undefined
        }
      });
      
      const fetchedRules = response.data.rules || [];
      setRules(fetchedRules);
      
      // Calculate stats
      const agentCount = fetchedRules.filter(r => r.user_type === 'agent').length;
      const b2cCount = fetchedRules.filter(r => r.user_type === 'b2c').length;
      const activeCount = fetchedRules.filter(r => r.is_active).length;
      
      setStats({
        total: fetchedRules.length,
        active: activeCount,
        agent_rules: agentCount,
        b2c_rules: b2cCount
      });
    } catch (error) {
      console.error('Error fetching markup rules:', error);
      alert('Failed to fetch markup rules');
    } finally {
      setLoading(false);
    }
  };

  // Fetch agents for special agent selection
  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/agents`, {
        withCredentials: true,
        params: { limit: 100, status: 'active' }
      });
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchAgents();
  }, [userTypeFilter, categoryFilter]);

  // Reset form
  const resetForm = () => {
    setFormData({
      user_type: 'agent',
      category: 'airline',
      value: 0,
      value_type: 'percentage',
      airline_code: '',
      route: '',
      travel_class: '',
      flight_type: '',  // NEW
      trip_type: '',    // NEW
      special_agents: [],
      priority: 1,
      valid_from: '',
      valid_to: ''
    });
  };

  // Create markup rule
  const createRule = async () => {
    try {
      const payload = { ...formData };
      
      // Clean up null/empty fields based on category.
      // Category "all" = global — strip all field-specific filters.
      if (payload.category === 'all') {
        delete payload.airline_code;
        delete payload.route;
        delete payload.travel_class;
      } else {
        if (payload.category !== 'airline' && payload.category !== 'combined') delete payload.airline_code;
        if (payload.category !== 'route' && payload.category !== 'combined') delete payload.route;
        if (payload.category !== 'class' && payload.category !== 'combined') delete payload.travel_class;
      }
      
      await axios.post(
        `${BACKEND_URL}/api/admin/markup`,
        null,
        {
          withCredentials: true,
          params: payload
        }
      );
      
      alert('Markup rule created successfully');
      setCreateModal(false);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Error creating markup rule:', error);
      alert(error.response?.data?.detail || 'Failed to create markup rule');
    }
  };

  // Update markup rule
  const updateRule = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/markup/${editModal.id}`,
        null,
        {
          withCredentials: true,
          params: {
            value: formData.value,
            value_type: formData.value_type,
            priority: formData.priority,
            is_active: formData.is_active,
            special_agents: formData.special_agents
          }
        }
      );
      
      alert('Markup rule updated successfully');
      setEditModal(null);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Error updating markup rule:', error);
      alert('Failed to update markup rule');
    }
  };

  // Delete markup rule
  const deleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this markup rule?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/markup/${ruleId}`, {
        withCredentials: true
      });
      
      alert('Markup rule deleted successfully');
      fetchRules();
    } catch (error) {
      console.error('Error deleting markup rule:', error);
      alert('Failed to delete markup rule');
    }
  };

  // Toggle rule active status
  const toggleRuleStatus = async (rule) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/markup/${rule.id}`,
        null,
        {
          withCredentials: true,
          params: { is_active: !rule.is_active }
        }
      );
      
      fetchRules();
    } catch (error) {
      console.error('Error toggling rule status:', error);
      alert('Failed to toggle rule status');
    }
  };

  // Open edit modal
  const openEditModal = (rule) => {
    setEditModal(rule);
    setFormData({
      user_type: rule.user_type,
      category: rule.category,
      value: rule.value,
      value_type: rule.value_type,
      airline_code: rule.airline_code || '',
      route: rule.route || '',
      travel_class: rule.travel_class || '',
      flight_type: rule.flight_type || '',  // NEW
      trip_type: rule.trip_type || '',      // NEW
      special_agents: rule.special_agents || [],
      priority: rule.priority,
      is_active: rule.is_active,
      valid_from: rule.valid_from || '',
      valid_to: rule.valid_to || ''
    });
  };

  // Get rule description
  const getRuleDescription = (rule) => {
    let desc = [];
    if (rule.airline_code) desc.push(`Airline: ${rule.airline_code}`);
    if (rule.route) desc.push(`Route: ${rule.route}`);
    if (rule.travel_class) desc.push(`Class: ${rule.travel_class}`);
    if (rule.flight_type) desc.push(`Flight: ${rule.flight_type.charAt(0).toUpperCase() + rule.flight_type.slice(1)}`);  // NEW
    if (rule.trip_type) desc.push(`Trip: ${rule.trip_type === 'oneway' ? 'One-Way' : 'Return'}`);  // NEW
    if (rule.special_agents && rule.special_agents.length > 0) {
      desc.push(`Special Agents: ${rule.special_agents.length}`);
    }
    return desc.join(' • ') || 'General Rule';
  };

  // Filter rules by search
  const filteredRules = rules.filter(rule => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      rule.airline_code?.toLowerCase().includes(search) ||
      rule.route?.toLowerCase().includes(search) ||
      rule.travel_class?.toLowerCase().includes(search) ||
      rule.category.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Markup Settings</h1>
          <p className="text-gray-500">Configure dynamic markup rules for agents and B2C users</p>
        </div>
        <button
          onClick={() => { resetForm(); setCreateModal(true); }}
          className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Rule
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Rules</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Tag className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Active Rules</p>
              <p className="text-3xl font-bold text-green-600">{stats.active}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Agent Rules</p>
              <p className="text-3xl font-bold text-purple-600">{stats.agent_rules}</p>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">B2C Rules</p>
              <p className="text-3xl font-bold text-orange-600">{stats.b2c_rules}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by airline, route, or class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <select
            value={userTypeFilter}
            onChange={(e) => setUserTypeFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">All User Types</option>
            <option value="agent">Agent</option>
            <option value="b2c">B2C</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">All Categories</option>
            <option value="all">All (every flight)</option>
            <option value="airline">Airline</option>
            <option value="route">Route</option>
            <option value="class">Class</option>
            <option value="combined">Combined</option>
          </select>

          <button
            onClick={fetchRules}
            className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Rules Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rule Details</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Markup Value</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading rules...
                  </td>
                </tr>
              ) : filteredRules.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No markup rules found
                  </td>
                </tr>
              ) : (
                filteredRules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">{getRuleDescription(rule)}</p>
                        {rule.valid_from && (
                          <p className="text-xs text-gray-500 mt-1">
                            Valid: {new Date(rule.valid_from).toLocaleDateString()} 
                            {rule.valid_to && ` - ${new Date(rule.valid_to).toLocaleDateString()}`}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        rule.user_type === 'agent' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {rule.user_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                        {rule.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {rule.value_type === 'percentage' ? `${rule.value}%` : `₹${rule.value}`}
                        </p>
                        <p className="text-xs text-gray-500">{rule.value_type}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="font-semibold text-gray-900">{rule.priority}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleRuleStatus(rule)}
                        className="flex items-center gap-2"
                      >
                        {rule.is_active ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-semibold text-green-700">Active</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-500">Inactive</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(rule)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit Rule"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => deleteRule(rule.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Rule"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCreateModal(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Create Markup Rule</h2>
              <p className="text-sm text-gray-500 mt-1">Configure dynamic markup for flights</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* User Type & Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">User Type</label>
                  <select
                    value={formData.user_type}
                    onChange={(e) => setFormData({...formData, user_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="agent">Agent</option>
                    <option value="b2c">B2C</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="markup-category-select"
                  >
                    <option value="all">All (applies to every flight)</option>
                    <option value="airline">Airline</option>
                    <option value="route">Route</option>
                    <option value="class">Class</option>
                    <option value="combined">Combined</option>
                  </select>
                </div>
              </div>

              {/* Conditional Fields Based on Category */}
              {(formData.category === 'airline' || formData.category === 'combined') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Airline Code</label>
                  <select
                    value={formData.airline_code}
                    onChange={(e) => setFormData({...formData, airline_code: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Select Airline</option>
                    {AIRLINES.map(airline => (
                      <option key={airline.code} value={airline.code}>
                        {airline.code} - {airline.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(formData.category === 'route' || formData.category === 'combined') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Route</label>
                  <input
                    type="text"
                    placeholder="e.g., BOM-DXB or DEL-* (wildcard)"
                    value={formData.route}
                    onChange={(e) => setFormData({...formData, route: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use * for wildcard (e.g., BOM-* for all routes from Mumbai)</p>
                </div>
              )}

              {(formData.category === 'class' || formData.category === 'combined') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Travel Class</label>
                  <select
                    value={formData.travel_class}
                    onChange={(e) => setFormData({...formData, travel_class: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Select Class</option>
                    {TRAVEL_CLASSES.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* NEW: Flight Type & Trip Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Flight Type <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <select
                    value={formData.flight_type}
                    onChange={(e) => setFormData({...formData, flight_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">All Flights</option>
                    <option value="domestic">Domestic Only</option>
                    <option value="international">International Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Trip Type <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <select
                    value={formData.trip_type}
                    onChange={(e) => setFormData({...formData, trip_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">All Trip Types</option>
                    <option value="oneway">One-Way Only</option>
                    <option value="return">Return/Round-Trip Only</option>
                  </select>
                </div>
              </div>

              {/* Markup Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Markup Type</label>
                  <select
                    value={formData.value_type}
                    onChange={(e) => setFormData({...formData, value_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Markup Value {formData.value_type === 'percentage' ? '(%)' : '(₹)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Priority (1-10, higher = more important)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 1})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Special Agents (only for agent user_type) */}
              {formData.user_type === 'agent' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Special Agents (Optional - leave empty for all agents)
                  </label>
                  <select
                    multiple
                    value={formData.special_agents}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({...formData, special_agents: selected});
                    }}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[120px]"
                  >
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.agency_name} - {agent.full_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple agents</p>
                </div>
              )}

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Valid From (Optional)</label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Valid To (Optional)</label>
                  <input
                    type="date"
                    value={formData.valid_to}
                    onChange={(e) => setFormData({...formData, valid_to: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setCreateModal(false); resetForm(); }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={createRule}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Create Rule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal (similar to create, but with update function) */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Edit Markup Rule</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Markup Type</label>
                  <select
                    value={formData.value_type}
                    onChange={(e) => setFormData({...formData, value_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Markup Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 1})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* NEW: Flight Type & Trip Type in Edit Modal */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Flight Type <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <select
                    value={formData.flight_type}
                    onChange={(e) => setFormData({...formData, flight_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">All Flights</option>
                    <option value="domestic">Domestic Only</option>
                    <option value="international">International Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Trip Type <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <select
                    value={formData.trip_type}
                    onChange={(e) => setFormData({...formData, trip_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">All Trip Types</option>
                    <option value="oneway">One-Way Only</option>
                    <option value="return">Return/Round-Trip Only</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {formData.user_type === 'agent' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Special Agents</label>
                  <select
                    multiple
                    value={formData.special_agents}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({...formData, special_agents: selected});
                    }}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[120px]"
                  >
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.agency_name} - {agent.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setEditModal(null); resetForm(); }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={updateRule}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkupSettings;
